import React, { useMemo, useState } from 'react';
import Typography from '@mui/material/Typography';
import { useFeathers } from '../../app/util';
import {
  CopyText,
  ReferenceEditor,
  TaskPage
 } from '../../components'
import styled from 'styled-components';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Link } from 'react-router-dom';

const findWebsiteCategoryTypes = ['website', 'social', 'news'];

const sourceHasUrl = (source) => {
  const url = typeof source === 'object' ? source?.url : source;
  return String(url || '').trim().length > 0;
};

const normalizeReferencesForSave = (references = {}) => {
  const categoriesByType = (references.categories || []).reduce((acc, category) => ({
    ...acc,
    [category.type]: category,
  }), {});

  const categoryTypes = [
    ...findWebsiteCategoryTypes,
    ...(references.categories || [])
      .map(category => category.type)
      .filter(type => !findWebsiteCategoryTypes.includes(type)),
  ];

  const categories = categoryTypes.map(type => {
    const category = categoriesByType[type] || { type, sources: [] };
    const sources = (category.sources || []).filter(sourceHasUrl);

    return {
      ...category,
      sources,
      missing: sources.length === 0,
    };
  });

  return {
    checked: categories.every(category => category.missing || category.sources.length > 0),
    totalSources: categories.reduce((acc, category) => acc + category.sources.length, 0),
    categories,
  };
};

const FindWebsites = () => {
  const feathers = useFeathers();

  const [ task, setTask ] = useState(null);
  const [ error, setError ] = useState(false);
  const [ candidate, setCandidate ] = useState(null);
  const [ race, setRace ] = useState(null);
  const [ saving, setSaving ] = useState(false);
  const [ referencesInput, setReferencesInput ] = useState({});
  const [ removedAutoContent, setRemovedAutoContent ] = useState({});
  const referencesForSave = useMemo(
    () => ({
      ...normalizeReferencesForSave(referencesInput),
      removedAutoContent,
    }),
    [referencesInput, removedAutoContent]
  );

  const activeDraft = useMemo(() => ({
    ...(candidate?.stagingDraft || {}),
    references: referencesInput || {},
    issues: candidate?.stagingDraft?.issues || candidate?.issues || [],
    bios: candidate?.stagingDraft?.bios || candidate?.bios || [],
    removedAutoContent,
  }), [candidate, referencesInput, removedAutoContent]);

  const onCheckoutTask = async (task, loadNextTask) => {
    setReferencesInput({})
    setRemovedAutoContent({})
    setCandidate(null)
    setRace(null)
    
    setTask(task);

    let candidate; 
    try {
      candidate = await feathers.getService('candidates').get(task.details.candidate);
    } catch(err) {
      if(err.name === 'NotFound') {
        // bad candidate, we need to cancel this task
        console.log('Candidate not found')
        await feathers.getService('research-tasks').patch(task._id, {
          $cancelTask: true
        });
        setTask(null);
        loadNextTask()
        return
      } else {
        throw err;
      }
    }
    console.log(candidate)
    setCandidate(candidate);
    // seed inputs with most recently updated data from holdingData or candidate record
    const useHoldingData = task?.holdingData && task?.holdingData?.updatedAt > candidate?.stagingDraft?.updatedAt;
    if (useHoldingData) {
      setReferencesInput(task.holdingData);
      setRemovedAutoContent(task.holdingData.removedAutoContent || candidate.removedAutoContent || {});
    } else {
      if(candidate?.stagingDraft?.references) setReferencesInput(candidate.stagingDraft.references);
      setRemovedAutoContent(candidate.removedAutoContent || {});
    }

    const raceId = candidate.race;
    if (raceId) {
      const race = await feathers.getService('races').get(raceId);
      setRace(race)
    }
  }

  return (
    <TaskPage
      taskType='find-website'
      errorMessage={error?.message}
      onCloseErrorMessage={setError}
      onCheckoutTask={onCheckoutTask}
      completionData={referencesForSave}
      holdingData={referencesForSave}
      noMoreTasksMessage={'There are currently no more candidates to find websites for. Try back later.'}
      task={task}
      onChangeSaving={setSaving}
    >
      <WrapperInner>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px'}}>
          <Typography variant='h3'>{candidate?.name}</Typography>
          <Link to={`/elections/${candidate?.election?.key || candidate?.election}/races/${candidate?.race}/candidates/${candidate?._id}`} target='_blank'>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}>
              <Typography variant='body2'>Full page</Typography>
              <OpenInNewIcon style={{ width: '15px', height: '15px' }}/>
            </div>
          </Link>
        </div>
        <Typography variant='body1' style={{ marginTop: '8px'}}>
            Priority: <b>{candidate?.priorityLevel || '--'}</b>
        </Typography>
        <Typography variant='body1' style={{ marginTop: '8px'}}>
          Running for {race?.office.name || ''} in {race?.district?.longName || ''}
        </Typography>
        <div style={{ marginTop: '8px' }}>
          <CopyText
            multiline
            text={`${candidate?.name} ${race?.longName || ''}`}
          />
        </div>
        <div style={{ padding: '36px', marginTop: '36px', borderRadius: '8px', backgroundColor: '#C3CEEC'}}>
          <ReferenceEditor
            priorityLevel={candidate?.priorityLevel}
            activeDraft={activeDraft}
            onChangeActiveDraft={(update) => {
              setReferencesInput(update.references || {});
              setRemovedAutoContent(update.removedAutoContent || {});
            }}
            disabled={saving}
          />
        </div>
      </WrapperInner>
    </TaskPage>
  )
}

const WrapperInner = styled.div`
  max-width: 700px;
  display: flex;
  flex-direction: column;
  padding: 24px 24px 0;

  align-items: stretch;
`

export default FindWebsites;
